/**
 * ExercisesCatalog — ЕДИНЫЙ каталог упражнений (4 модуля: Речь / Когнитивные
 * навыки / Мимика / Моторика). Одна реализация — одна точка входа:
 *   страница /exercises — через ExercisesPage (пилюля «Тренажёры» ЛК пациента
 *   ведёт именно сюда, отдельной вкладки в кабинете больше нет).
 * Поиск: обычно живёт в строке заголовка страницы (props query + onQueryChange).
 * Если props не переданы — поле поиска рисуется внутри каталога (резервный режим).
 * Данные — единственный источник: ../exercises-data (как на странице врача).
 * Яркие крупные кнопки для пожилых пациентов; галочка «Включить в ИПР»
 * сохраняется сразу (getCvIpr/putCvIpr); у «Мимики» — Калибровка и Параметры.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight, BookOpen, Brain, BrainCircuit, ChevronDown, Hand, RefreshCw,
  Search, SlidersHorizontal, Smile, Speech,
} from 'lucide-react';
import { exercisesCatalog } from '../exercises-data';
import { getCvIpr, putCvIpr } from '../lib/cortivoxApi';
import ExerciseParamsDialog from './ExerciseParamsDialog';


const domainIcons = {
  'Речь': Speech,
  'Когнитивные навыки': Brain,
  'Мимика': Smile,
  'Моторика': Hand,
  'Связная речь': BookOpen,
};

// 4 модуля в заданном порядке; неожиданные домены не теряем — добавляем в конец.
const CANON = ['Речь', 'Когнитивные навыки', 'Мимика', 'Моторика'];

const CAT = Array.isArray(exercisesCatalog) ? exercisesCatalog : [];

const sections = (() => {
  const out = CANON.map((domain) => ({
    title: domain,
    icon: domainIcons[domain] || BrainCircuit,
    exercises: CAT.filter((ex) => ex.domain === domain),
  }));
  // неожиданные домены не теряем — добавляем в конец
  CAT.forEach((ex) => {
    if (ex?.domain && !out.some((s) => s.title === ex.domain)) {
      out.push({ title: ex.domain, icon: BrainCircuit, exercises: CAT.filter((e) => e.domain === ex.domain) });
    }
  });
  return out;
})();

// 404 — на сервере ещё старый cv_reports.py: маршрута /ipr там нет.
const describeIprError = (e) =>
  e?.status === 404
    ? 'План недоступен: сервер ещё не обновлён (нужен новый cv_reports.py и рестарт API)'
    : e?.message || 'Не удалось получить план реабилитации';

export default function ExercisesCatalog({ embedded = false, query: queryProp, onQueryChange }) {
  // Поиск — управляемый (props query/onQueryChange, поле в строке заголовка страницы)
  // или внутренний (резервный режим: поле рисуется внутри каталога).
  const isControlled = typeof queryProp === 'string' && typeof onQueryChange === 'function';
  const [ownQuery, setOwnQuery] = useState('');
  const query = isControlled ? queryProp : ownQuery;
  const setQuery = isControlled ? onQueryChange : setOwnQuery;
  // Какой модуль раскрыт (индекс; -1 — ВСЕ СВЁРНУТЫ). Врач: при открытии каталога
  // все разделы должны быть закрыты — раскрывается только выбранный вручную.
  const [openSection, setOpenSection] = useState(-1);
  // Упражнение, у которого открыт диалог «Параметры» (количество повторов и т.д.)
  const [paramsFor, setParamsFor] = useState(null);
  // ИПР: слаги, отмеченные в Каталоге. null — ещё грузится, [] — пусто.
  const [iprSlugs, setIprSlugs] = useState(null);
  const [iprError, setIprError] = useState('');
  // «Калибровка заново»: флаг для следующего упражнения (см. ArticulationGame).
  const [recalDone, setRecalDone] = useState(false);

  useEffect(() => {
    let alive = true;
    getCvIpr()
      .then((res) => { if (alive) setIprSlugs((res?.items || []).map((i) => i.exerciseSlug)); })
      .catch((e) => { if (alive) { setIprSlugs([]); setIprError(describeIprError(e)); } });
    return () => { alive = false; };
  }, []);

  // Отметили/сняли галочку «Включить в ИПР» — план сохраняется сразу,
  // отмеченное упражнение появляется в ИПР в ЛК без дополнительных действий.
  const toggleIpr = async (slug) => {
    if (!Array.isArray(iprSlugs)) return;
    const has = iprSlugs.includes(slug);
    const prev = iprSlugs;
    const next = has ? iprSlugs.filter((s) => s !== slug) : [...iprSlugs, slug];
    setIprSlugs(next);
    try { await putCvIpr(next); } catch (e) { setIprSlugs(prev); setIprError(describeIprError(e)); }
  };

  // Калибровка (модуль «Мимика»): флаг в localStorage — следующее упражнение
  // калибуется заново, новая калибровка сохранится вместо прежней.
  const requestRecalibration = () => {
    try { localStorage.setItem('cvRecalibrate', '1'); } catch { /* хранилище недоступно */ }
    setRecalDone(true);
  };

  const q = query.trim().toLowerCase();
  const matchQ = (ex) => !q
    || String(ex.title || '').toLowerCase().includes(q)
    || String(ex.description || '').toLowerCase().includes(q);

  return (
    <div className={embedded ? 'flex flex-col gap-5' : 'flex flex-col gap-4'}>
      {/* Поиск — только в резервном режиме (обычно поле живёт в строке заголовка страницы) */}
      {!isControlled && (
        <label className="relative block w-full md:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-cvx-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Поиск упражнения..."
            aria-label="Поиск упражнения"
            className="h-12 w-full rounded-2xl border border-cvx-border bg-cvx-card pl-10 pr-4 text-base text-cvx-foreground outline-none transition-all placeholder:text-cvx-muted-foreground/70 focus:border-cvx-primary/50 focus:ring-4 focus:ring-cvx-ring/20"
          />
        </label>
      )}

      {iprError && <p className="-mt-2 text-sm text-cvx-destructive">{iprError}</p>}

      <div className="flex flex-col gap-3">
        {sections.map((section, index) => {
          const list = section.exercises.filter(matchQ);
          const isOpen = openSection === index;
          if (q && list.length === 0) return null;
          const SectionIcon = section.icon;
          return (
            <div key={section.title} className="cv-shadow overflow-hidden rounded-3xl border border-cvx-border bg-cvx-card">
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setOpenSection(isOpen ? -1 : index)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-cvx-secondary/50 sm:px-6"
              >
                <span className="flex min-w-0 items-center gap-4">
                  <span className={`flex size-12 shrink-0 items-center justify-center rounded-2xl ${isOpen ? 'bg-cvx-ink text-cvx-primary-foreground' : 'bg-cvx-accent text-cvx-accent-foreground'}`}>
                    <SectionIcon className="size-6" />
                  </span>
                  <span>
                    <span className="block text-xl font-bold text-cvx-ink">{section.title}</span>
                    <span className="mt-0.5 block text-sm text-cvx-muted-foreground">
                      {section.exercises.length} {pluralRu(section.exercises.length)}
                    </span>
                  </span>
                </span>
                <ChevronDown className={`size-5 shrink-0 text-cvx-muted-foreground transition-transform duration-300 ${isOpen ? 'rotate-180 text-cvx-ink' : ''}`} />
              </button>
              <div className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                <div className="min-h-0 overflow-hidden">
                  {/* Калибровка — в модуле «Мимика» (врач: «кнопка должна быть в Тренажёрах»):
                      проходится один раз и сохраняется, повторная — по решению пациента. */}
                  {section.title === 'Мимика' && isOpen && (
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-cvx-border bg-cvx-secondary/40 px-5 py-3 sm:px-6">
                      <p className="max-w-md text-sm text-cvx-muted-foreground">
                        Калибровка проходится один раз и сохраняется. Если лицо заметно изменилось
                        или оценка стала неточной — пройдите её заново.
                      </p>
                      <span className="flex shrink-0 items-center gap-3">
                        {recalDone && <span className="text-xs text-cvx-muted-foreground">Готово: следующее упражнение начнётся с калибровки</span>}
                        <button
                          type="button"
                          onClick={requestRecalibration}
                          className="inline-flex items-center gap-1.5 rounded-2xl border border-cvx-border bg-cvx-card px-4 py-2.5 text-sm font-bold text-cvx-foreground transition-colors hover:bg-cvx-secondary"
                        >
                          <RefreshCw className="size-4" /> Калибровка
                        </button>
                      </span>
                    </div>
                  )}
                  <div className="grid gap-4 border-t border-cvx-border bg-cvx-secondary/20 p-4 sm:p-5 lg:grid-cols-2 xl:grid-cols-3">
                    {list.map((ex) => (
                      <article key={ex.id} className="cv-shadow flex flex-col rounded-2xl border border-cvx-border bg-cvx-card p-5 transition-all hover:-translate-y-0.5 hover:border-cvx-primary/40">
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="text-xl font-bold leading-tight text-cvx-ink">{ex.title}</h3>
                          <span className="shrink-0 text-4xl leading-none" aria-hidden="true">{ex.icon || '🎯'}</span>
                        </div>

                        <p className="mt-2 min-h-14 text-base leading-6 text-cvx-muted-foreground">{ex.description}</p>

                        {/* Яркая крупная кнопка — для пожилых пациентов */}
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                          <Link
                            to={ex.href || `/exercise/${ex.id}`}
                            className="inline-flex h-14 flex-1 items-center justify-center gap-2 rounded-2xl bg-cvx-primary px-5 text-lg font-bold text-cvx-primary-foreground shadow transition-all hover:-translate-y-0.5 hover:bg-cvx-primary/90"
                          >
                            Тренироваться <ArrowUpRight className="size-5" />
                          </Link>
                          {/* Параметры — у упражнений Мимики (CV-тренажёр):
                              количество повторов и т.д. (ExerciseParamsDialog). */}
                          {section.title === 'Мимика' && (
                            <button
                              type="button"
                              onClick={() => setParamsFor(ex)}
                              aria-label={`Параметры: ${ex.title}`}
                              className="inline-flex size-14 items-center justify-center rounded-2xl border border-cvx-border text-cvx-ink transition-colors hover:border-cvx-primary/50 hover:bg-cvx-secondary/50"
                            >
                              <SlidersHorizontal className="size-5" />
                            </button>
                          )}
                        </div>

                        {/* Включить в ИПР — на КАЖДОЙ плашке (врач: у всех, а не только Мимики).
                            Отмеченное сразу попадает в план (ЛК → ИПР). */}
                        <label className="mt-3 inline-flex cursor-pointer select-none items-center gap-2 text-sm font-medium text-cvx-muted-foreground">
                          <input
                            type="checkbox"
                            checked={Array.isArray(iprSlugs) && iprSlugs.includes(ex.id)}
                            disabled={!Array.isArray(iprSlugs)}
                            onChange={() => toggleIpr(ex.id)}
                            className="size-5 cursor-pointer accent-[#216858]"
                          />
                          Включить в ИПР
                        </label>
                      </article>
                    ))}
                    {list.length === 0 && !q && (
                      <p className="py-4 text-sm text-cvx-muted-foreground">В этом модуле пока нет упражнений.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Диалог «Параметры» у плашки упражнения (Мимика) */}
      {paramsFor && (
        <ExerciseParamsDialog
          slug={paramsFor.id}
          title={paramsFor.title}
          onClose={() => setParamsFor(null)}
        />
      )}
    </div>
  );
}

function pluralRu(n) {
  const a = n % 10, b = n % 100;
  if (a === 1 && b !== 11) return 'упражнение';
  if (a >= 2 && a <= 4 && (b < 12 || b > 14)) return 'упражнения';
  return 'упражнений';
}
